import { useMemo } from "react";
import { useAsyncPaginatedData } from "../../../../hooks/useAsyncPaginatedData";
import { usePolling } from "../../../../hooks/usePolling";
import type { ListPagination } from "../../../../lib/pagination";
import {
  listBatchJobItems,
  type BatchJob,
  type ListBatchJobItemsParams,
} from "../../../../lib/corpusApi";
import type { BatchJobItemRow } from "./batch_job_item_rows";

const POLL_INTERVAL_MS = 30_000;
const RUNNING_STATUSES = new Set<BatchJob["status"]>(["queued", "running", "paused"]);
const PAGE_SIZE = 25;

export interface UseBatchJobItemListOptions<Result> {
  job: BatchJob;
  /** What the list is narrowed to, without its paging. */
  filters: Omit<ListBatchJobItemsParams, "limit" | "offset">;
  /** Changes whenever `filters` does, to send the list back to its first page. */
  resetKey: string;
  /** Narrows an item's generic result bag to the kind's result. Must be a stable function. */
  narrow: (raw: Record<string, unknown> | null) => Result | null;
}

export interface UseBatchJobItemListResult<Result> {
  rows: BatchJobItemRow<Result>[];
  loading: boolean;
  error: string | null;
  pagination: ListPagination;
  refresh: () => Promise<void>;
}

/** One page of a batch job's items at a time, narrowed to the kind's results. Refetches every 30s
 *  while the job is queued or running — new items keep landing as it works through its subjects. */
export function useBatchJobItemList<Result>({
  job,
  filters,
  resetKey,
  narrow,
}: UseBatchJobItemListOptions<Result>): UseBatchJobItemListResult<Result> {
  const { items, loading, error, pagination, refresh } = useAsyncPaginatedData(
    (offsetPage) => listBatchJobItems(job.id, { ...filters, ...offsetPage }),
    { pageSize: PAGE_SIZE, key: resetKey }
  );

  usePolling(refresh, POLL_INTERVAL_MS, { enabled: RUNNING_STATUSES.has(job.status) });

  const rows = useMemo(
    () => items.map((item) => ({ item, result: narrow(item.result) })),
    [items, narrow]
  );

  return { rows, loading, error, pagination, refresh };
}
